import { Component, computed, input, output } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import type { RegbrFilterOption } from '../regbr.models';
import { flattenRegbrOptions } from '../regbr.service';

@Component({
  selector: 'app-regbr-filter-list',
  standalone: true,
  imports: [TranslocoPipe],
  template: `
    <div class="space-y-2">
      <div class="flex items-center justify-between gap-2">
        <p class="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {{ label() }}
        </p>
        <button
          type="button"
          (click)="toggleAll()"
          class="text-[11px] font-medium text-muted-foreground transition hover:text-primary"
        >
          {{
            allSelected()
              ? ('pages.regbr.filters.clear' | transloco)
              : ('pages.regbr.filters.selectAll' | transloco)
          }}
        </button>
      </div>

      <div class="max-h-72 space-y-1 overflow-auto rounded-xl border border-border bg-background p-2">
        @for (option of options(); track option.value) {
          @if (option.items?.length) {
            <div class="pt-1 first:pt-0">
              <p class="px-2 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {{ option.label }}
              </p>
              @for (child of option.items; track child.value) {
                <button
                  type="button"
                  (click)="toggle(child.value)"
                  class="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm transition hover:bg-muted"
                >
                  <span
                    class="flex h-4 w-4 shrink-0 items-center justify-center rounded border"
                    [class.border-primary]="selected(child.value)"
                    [class.bg-primary]="selected(child.value)"
                    [class.text-primary-foreground]="selected(child.value)"
                    [class.border-muted-foreground/40]="!selected(child.value)"
                  >
                    @if (selected(child.value)) {
                      <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" class="h-3 w-3">
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    }
                  </span>
                  <span class="truncate text-foreground">{{ child.label }}</span>
                </button>
              }
            </div>
          } @else {
            <button
              type="button"
              (click)="toggle(option.value)"
              class="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm transition hover:bg-muted"
            >
              <span
                class="flex h-4 w-4 shrink-0 items-center justify-center rounded border"
                [class.border-primary]="selected(option.value)"
                [class.bg-primary]="selected(option.value)"
                [class.text-primary-foreground]="selected(option.value)"
                [class.border-muted-foreground/40]="!selected(option.value)"
              >
                @if (selected(option.value)) {
                  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" class="h-3 w-3">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                }
              </span>
              <span class="truncate text-foreground">{{ option.label }}</span>
            </button>
          }
        } @empty {
          <p class="px-3 py-4 text-center text-xs text-muted-foreground">
            {{ 'pages.regbr.filters.loading' | transloco }}
          </p>
        }
      </div>

      <p class="text-[11px] text-muted-foreground">
        {{ values().length }} / {{ flatOptions().length }}
        {{ 'pages.regbr.filters.selected' | transloco }}
      </p>
    </div>
  `,
})
export class RegbrFilterListComponent {
  readonly label = input('');
  readonly options = input<RegbrFilterOption[]>([]);
  readonly values = input<string[]>([]);
  readonly valuesChange = output<string[]>();

  protected readonly flatOptions = computed(() => flattenRegbrOptions(this.options()));
  protected readonly allSelected = computed(
    () =>
      this.flatOptions().length > 0 &&
      this.values().length === this.flatOptions().length,
  );

  protected selected(value: string): boolean {
    return this.values().includes(value);
  }

  protected toggle(value: string): void {
    const values = this.values();
    this.valuesChange.emit(
      values.includes(value)
        ? values.filter((item) => item !== value)
        : [...values, value],
    );
  }

  protected toggleAll(): void {
    this.valuesChange.emit(
      this.allSelected() ? [] : this.flatOptions().map((option) => option.value),
    );
  }
}
